import React from "react";
import {
  Repeat,
  Target,
  GraduationCap,
  Handshake,
  ArrowUpRight,
  Check,
  Star,
} from "lucide-react";
import { useContact } from "@/contexts/ContactContext";
import { Reveal } from "@/components/Reveal";

const MODELS = [
  {
    n: "01",
    icon: Repeat,
    title: "Growth Retainers",
    tagline: "Embedded growth team, on a fixed monthly cadence.",
    body: "An always-on cross-functional team running your brand, content, performance and partnerships pipeline — so growth compounds month over month.",
    commitment: "3 – 12 months",
    bestFor: "Brands building a long-term moat",
    highlights: [
      "Dedicated strategy, creative & performance pod",
      "Monthly campaign cycles + KPI reviews",
      "Priority on production & sponsorship pipeline",
    ],
    featured: true,
    testid: "engagement-01",
  },
  {
    n: "02",
    icon: Target,
    title: "Project Sprints",
    tagline: "Defined scope. Sharp timeline. Premium output.",
    body: "Single-track engagements for launches, brand films, campaigns and event activations — clear deliverables and a fixed turnaround.",
    commitment: "4 – 12 weeks",
    bestFor: "Launches, films & activations",
    highlights: [
      "Brief · script · shoot · ship",
      "Fixed scope and fixed timeline",
      "Senior creatives on every project",
    ],
    testid: "engagement-02",
  },
  {
    n: "03",
    icon: GraduationCap,
    title: "Advisory & Workshops",
    tagline: "Strategy sprints and creative training for your team.",
    body: "Leadership intensives, GTM strategy sessions and hands-on creative workshops that upskill your internal team and align your roadmap.",
    commitment: "1 – 4 weeks",
    bestFor: "Founders & internal teams levelling up",
    highlights: [
      "Founder & leadership intensives",
      "Brand, GTM & content strategy sprints",
      "Live creative & production workshops",
    ],
    testid: "engagement-03",
  },
  {
    n: "04",
    icon: Handshake,
    title: "Sponsorship Partnership",
    tagline: "Performance-linked sponsorship management, end-to-end.",
    body: "Percentage-based revenue model — we source, negotiate, activate and report on sponsorships across your events, IP and properties.",
    commitment: "Ongoing · % based",
    bestFor: "Events, IP owners & creators",
    highlights: [
      "Sponsor sourcing & negotiation",
      "Activation & on-ground delivery",
      "Post-event ROI & reporting",
    ],
    testid: "engagement-04",
  },
];

const STEPS = [
  { label: "Discover", sub: "Goals, audience, KPIs" },
  { label: "Design", sub: "Strategy & creative" },
  { label: "Deploy", sub: "Production & launch" },
  { label: "Optimise", sub: "Measure & scale" },
];

export const HowWeWork = () => {
  const { openContact } = useContact();
  return (
    <section
      id="how-we-work"
      data-testid="how-we-work-section"
      className="relative bg-[#000000] py-16 md:py-24 lg:py-28 border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_top,rgba(146,24,136,0.08),transparent_55%)]" />

      <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6 md:px-12 lg:px-16">
        {/* Heading */}
        <Reveal className="text-center mb-12 md:mb-14">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10 bg-fuchsia-500" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-fuchsia-400 font-bold">
              How We Work
            </span>
            <span className="h-px w-10 bg-fuchsia-500" />
          </div>
          <h2
            data-testid="how-we-work-headline"
            className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-[64px] font-black tracking-[-0.03em] text-white leading-[0.98] max-w-5xl mx-auto"
          >
            Engagement models that{" "}
            <span className="text-fuchsia-500 italic font-light">flex with you.</span>
          </h2>
          <p className="mt-5 text-sm md:text-base text-white/55 leading-relaxed font-body max-w-3xl mx-auto">
            One predictable process, four ways to engage. Pick the model that
            matches your stage — start small, scale as you grow, or combine
            them into a single growth engine.
          </p>
        </Reveal>

        {/* Process strip */}
        <Reveal
          className="mx-auto max-w-5xl mb-14 md:mb-20 grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-3"
          data-testid="how-we-work-process"
          delay={80}
        >
          {STEPS.map((s, i) => (
            <div
              key={s.label}
              className="relative flex flex-col items-center text-center gap-2 group"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white/15 bg-white/[0.03] text-fuchsia-400 font-display text-xs font-bold group-hover:border-fuchsia-500/50 group-hover:bg-fuchsia-500/10 transition-colors">
                0{i + 1}
              </div>
              <div className="text-xs md:text-sm uppercase tracking-[0.18em] text-white font-semibold">
                {s.label}
              </div>
              <div className="text-[11px] text-white/45 leading-tight">
                {s.sub}
              </div>
              {i < STEPS.length - 1 && (
                <span className="hidden md:block absolute top-[20px] left-[calc(50%+24px)] w-[calc(100%-48px)] h-px bg-gradient-to-r from-fuchsia-500/40 to-transparent" />
              )}
            </div>
          ))}
        </Reveal>

        {/* Bento grid of engagement models */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {MODELS.map((m, i) => (
            <Reveal key={m.n} variant="scale" delay={Math.min(i * 80, 240)}>
              <ModelCard m={m} openContact={openContact} />
            </Reveal>
          ))}
        </div>

        {/* Closing nudge */}
        <p className="mt-12 text-center text-sm text-white/45 max-w-2xl mx-auto leading-relaxed">
          Not sure which model fits?{" "}
          <button
            type="button"
            onClick={() => openContact({ source: "how-we-work-footer" })}
            data-testid="how-we-work-help-cta"
            className="text-fuchsia-400 hover:text-fuchsia-300 font-semibold underline-offset-4 hover:underline"
          >
            Tell us about your project
          </button>{" "}
          and we&apos;ll propose the right one.
        </p>
      </div>
    </section>
  );
};

const ModelCard = ({ m, openContact }) => {
  const Icon = m.icon;
  return (
    <div
      data-testid={m.testid}
      className={`group relative overflow-hidden border bg-[#0a0a0c] hover:-translate-y-1 transition-all duration-500 flex flex-col ${
        m.featured
          ? "border-fuchsia-500/40 hover:border-fuchsia-500/60 lg:scale-[1.005]"
          : "border-white/10 hover:border-fuchsia-500/40"
      }`}
    >
      {/* Featured ribbon */}
      {m.featured && (
        <div className="absolute top-0 right-0 z-20 bg-fuchsia-500 text-white text-[10px] uppercase tracking-[0.28em] font-bold px-3 py-1.5 flex items-center gap-1.5">
          <Star className="w-3 h-3 fill-white" />
          Most Popular
        </div>
      )}

      {/* Subtle accent gradient on featured */}
      {m.featured && (
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top_right,rgba(146,24,136,0.15),transparent_55%)]" />
      )}

      <div className="relative z-10 p-6 md:p-8 lg:p-9 flex flex-col flex-grow">
        {/* Top row */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div
              className={`w-11 h-11 flex items-center justify-center border ${
                m.featured
                  ? "border-fuchsia-500/40 bg-fuchsia-500/[0.12] text-fuchsia-400"
                  : "border-fuchsia-500/30 bg-fuchsia-500/[0.08] text-fuchsia-400"
              }`}
            >
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-fuchsia-400 font-bold leading-tight">
                Model · {m.n}
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mt-0.5">
                {m.commitment}
              </div>
            </div>
          </div>
          <span className="font-display text-2xl text-white/15 tracking-widest font-black">
            {m.n}
          </span>
        </div>

        {/* Title + tagline */}
        <h3 className="font-display text-2xl md:text-3xl lg:text-[36px] font-bold tracking-tight text-white leading-[1.05]">
          {m.title}
        </h3>
        <p className="mt-2 text-sm md:text-base text-fuchsia-300/85 italic font-light leading-snug">
          {m.tagline}
        </p>

        {/* Body */}
        <p className="mt-4 text-sm md:text-[15px] text-white/60 leading-relaxed">
          {m.body}
        </p>

        {/* Highlights */}
        <ul className="mt-6 space-y-2.5">
          {m.highlights.map((h) => (
            <li
              key={h}
              className="flex items-start gap-2.5 text-sm text-white/75"
            >
              <Check className="w-4 h-4 mt-0.5 text-fuchsia-400 shrink-0" />
              <span>{h}</span>
            </li>
          ))}
        </ul>

        {/* Footer */}
        <div className="mt-7 pt-5 border-t border-white/10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">
              Best for
            </div>
            <div className="text-sm text-white/85 font-medium leading-snug">
              {m.bestFor}
            </div>
          </div>
          <button
            type="button"
            onClick={() => openContact({ source: m.testid })}
            data-testid={`${m.testid}-cta`}
            className={`inline-flex w-full sm:w-auto items-center justify-center gap-2 text-sm font-semibold rounded-full transition-all sm:shrink-0 ${
              m.featured
                ? "bg-fuchsia-500 hover:bg-fuchsia-400 text-white px-5 py-2.5 ember-glow"
                : "text-fuchsia-400 hover:text-fuchsia-300 group/btn"
            }`}
          >
            {m.featured ? "Start here" : "Discuss"}
            <ArrowUpRight className="w-4 h-4 group-hover/btn:rotate-45 transition-transform" />
          </button>
        </div>
      </div>

      {/* Hover accent line */}
      <div className="absolute bottom-0 left-0 h-px w-0 bg-fuchsia-500 group-hover:w-full transition-all duration-700" />
    </div>
  );
};
